import type { PaymentIntentResult, Stripe, StripeCardNumberElement } from '@stripe/stripe-js';

import { toast } from 'react-toastify';

import type { BillingSubscribeResponse } from './billing-payment.types';

import { handleCardPaymentResultAsync } from './card-payment-result.util';

type CompleteSubscriptionPaymentParams = {
  cardholderName: string;
  clientSecret: string;
  confirmCardPayment: (
    variables: {
      cardholderName: string;
      cardNumber: StripeCardNumberElement;
      clientSecret: string;
    },
    options?: {
      onError?: () => void;
      onSuccess?: (payload?: PaymentIntentResult) => void;
    },
  ) => void;
  endPaymentFlow: () => void;
  fetchBillingSubscribeSecret?: () => Promise<BillingSubscribeResponse | undefined>;
  getCardNumberElement: () => null | StripeCardNumberElement;
  onPaymentSuccess: () => void;
  stripe: null | Stripe;
  subscriptionPaid?: boolean;
};

export const completeSubscriptionPayment = async ({
  subscriptionPaid,
  clientSecret,
  fetchBillingSubscribeSecret,
  getCardNumberElement,
  cardholderName,
  confirmCardPayment,
  onPaymentSuccess,
  endPaymentFlow,
  stripe,
}: CompleteSubscriptionPaymentParams): Promise<void> => {
  if (subscriptionPaid === true) {
    onPaymentSuccess();
    return;
  }

  let secretForSubscribe = clientSecret;

  if (fetchBillingSubscribeSecret) {
    try {
      const payload = await fetchBillingSubscribeSecret();

      if (payload?.alreadySubscribed) {
        onPaymentSuccess();
        return;
      }

      const next = payload?.clientSecret;
      if (typeof next !== 'string' || next.length === 0) {
        endPaymentFlow();
        toast('Unable to refresh payment session. Refresh the page or try again shortly.', {
          type: 'error',
          position: 'bottom-center',
        });
        return;
      }

      secretForSubscribe = next;
    } catch {
      endPaymentFlow();
      toast('Unable to refresh payment session. Refresh the page or try again shortly.', {
        type: 'error',
        position: 'bottom-center',
      });
      return;
    }
  }

  const subscriptionCardNumber = getCardNumberElement();
  if (!subscriptionCardNumber) {
    endPaymentFlow();
    toast('Payment form was reset. Please enter your card details and try again.', {
      type: 'error',
      position: 'bottom-center',
    });
    return;
  }

  confirmCardPayment(
    {
      cardholderName,
      cardNumber: subscriptionCardNumber,
      clientSecret: secretForSubscribe,
    },
    {
      onError: endPaymentFlow,
      onSuccess: (payload) => {
        void handleCardPaymentResultAsync({
          payload,
          stripe,
          clientSecret: secretForSubscribe,
          endPaymentFlow,
          onSuccess: onPaymentSuccess,
        });
      },
    },
  );
};
